import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge } from '@/components/ui';
import { adminStatsService, TopTenant } from '@/services/adminStats.service';

interface TopTenantsProps {
  className?: string;
}

// Mini Sparkline Component
interface SparklineProps {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  color: _color = '#3b82f6',
  width = 60,
  height = 20,
}) => {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const padding = 2;
  const innerWidth = width - padding * 2;
  const innerHeight = height - padding * 2;

  const points = data.map((value, i) => {
    const x = padding + (i / (data.length - 1)) * innerWidth;
    const y = padding + innerHeight - ((value - min) / range) * innerHeight;
    return `${x},${y}`;
  }).join(' ');

  // Determine if trending up or down
  const isUpward = data[data.length - 1] > data[0];
  const sparkColor = isUpward ? '#10b981' : '#ef4444';

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={sparkColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End point indicator */}
      <circle
        cx={padding + innerWidth}
        cy={padding + innerHeight - ((data[data.length - 1] - min) / range) * innerHeight}
        r="2"
        fill={sparkColor}
      />
    </svg>
  );
};

type SortBy = 'qLeadCount' | 'revenue' | 'activeBrokers';

export const TopTenants: React.FC<TopTenantsProps> = ({ className = '' }) => {
  const [tenants, setTenants] = useState<TopTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('qLeadCount');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await adminStatsService.getTopTenants(sortBy, 10);
        setTenants(data);
      } catch (err) {
        setError('Failed to load top tenants');
        console.error('Error fetching top tenants:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sortBy]);

  const getPlanBadgeVariant = (plan: string): 'success' | 'info' | 'warning' | 'default' => {
    switch (plan.toLowerCase()) {
      case 'enterprise':
        return 'success';
      case 'professional':
        return 'info';
      case 'starter':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value: number): string => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value}`;
  };

  if (error) {
    return (
      <Card className={className}>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">Top Performing Tenants</h2>
        </Card.Header>
        <Card.Body>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-danger-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="mt-2 text-sm text-secondary-600">{error}</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className} padding="none">
      <div className="px-6 py-4 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Top Performing Tenants</h2>
            <p className="text-sm text-secondary-500">Top 10 tenants by performance</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-secondary-500">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="text-sm border border-secondary-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="qLeadCount">QLeads</option>
              <option value="revenue">Revenue</option>
              <option value="activeBrokers">Active Brokers</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-secondary-200">
          <thead className="bg-secondary-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Rank
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Tenant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                QLeads
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Brokers
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                Trend
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-200">
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 w-8 bg-secondary-200 rounded animate-pulse" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 w-32 bg-secondary-200 rounded animate-pulse" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 w-16 bg-secondary-200 rounded animate-pulse" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 w-16 bg-secondary-200 rounded animate-pulse" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 w-12 bg-secondary-200 rounded animate-pulse" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-4 w-16 bg-secondary-200 rounded animate-pulse" />
                  </td>
                </tr>
              ))
            ) : tenants.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-secondary-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-secondary-500">No tenants found</p>
                </td>
              </tr>
            ) : (
              tenants.map((tenant, index) => (
                <tr
                  key={tenant.id}
                  className="hover:bg-secondary-50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0
                          ? 'bg-yellow-100 text-yellow-700'
                          : index === 1
                          ? 'bg-gray-100 text-gray-700'
                          : index === 2
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}
                    >
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link to={`/tenants/${tenant.id}`} className="group">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-primary-600">
                            {tenant.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-secondary-900 group-hover:text-primary-600 transition-colors">
                            {tenant.name}
                          </p>
                          <Badge variant={getPlanBadgeVariant(tenant.plan)} size="sm">
                            {tenant.plan}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-secondary-900">
                      {tenant.qLeadCount.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-success-600">
                      {formatCurrency(tenant.revenue)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <svg
                        className="w-4 h-4 text-secondary-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span className="text-sm text-secondary-900">{tenant.activeBrokers}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Sparkline data={tenant.trend} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-secondary-200 bg-secondary-50">
        <Link
          to="/tenants"
          className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center"
        >
          View all tenants
          <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Link>
      </div>
    </Card>
  );
};

export default TopTenants;
